import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';

import { queryTable, Table } from 'common/types/disco/table';

type Well = { id: string; color: string };
type HoverProps = {
  onWellEnter: (id: string | null, event: React.MouseEvent<any>) => void;
  onWellLeave: () => void;
};

type WithWellHoverProps = {
  location: string;
  wells: Well[];
  weightNames: string[];
  labels: string[];
  data?: Table;
  children: (hoverProps: HoverProps) => React.ReactNode;
};

type VirtualElement = {
  getBoundingClientRect: () => DOMRect;
};

export function PlateMapTooltip({
  location,
  wells,
  weightNames,
  labels,
  data,
  children,
}: WithWellHoverProps) {
  const [tooltip, setTooltip] = useState<string[]>([]);
  const [hovered, setHovered] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<VirtualElement | null>(null);

  const onWellEnter = (id: string | null, e: React.MouseEvent<HTMLElement>) => {
    setHovered(id);
    const virt: VirtualElement = {
      getBoundingClientRect: () =>
        ({
          top: e.clientY,
          bottom: e.clientY,
          left: e.clientX,
          right: e.clientX,
          width: 0,
          height: 0,
        } as DOMRect),
    };
    setAnchorEl(virt);
  };
  const onWellLeave = () => {
    setHovered(null);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (hovered) {
      const well = hovered ? wells.find(w => w.id === hovered) : null;
      if (well) {
        const query = queryTable(data);
        const row = query.findRow(location, well.id);
        if (row) {
          const weightIndices = query.columnIndices(...weightNames);
          const values = weightIndices.map(weight => row?.[weight]);
          const tooltips = labels.map((label, index) => `${label}: ${values[index]}`);
          setTooltip(tooltips);
        }
      }
    }
  }, [data, hovered, labels, location, weightNames, wells]);

  return (
    <>
      {children({ onWellEnter, onWellLeave })}

      <Popper
        open={!!hovered}
        anchorEl={anchorEl}
        placement="top"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        <Box
          sx={{
            p: 1,
            bgcolor: 'background.paper',
            boxShadow: 1,
            borderRadius: 1,
          }}
        >
          {tooltip.map((t, index) => (
            <p key={index}>{t}</p>
          ))}
        </Box>
      </Popper>
    </>
  );
}
